import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Gift, Heart, Flame, Star, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const DiwaliLanding = () => {
  const navigate = useNavigate();
  const [litDiyas, setLitDiyas] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [couponRevealed, setCouponRevealed] = useState(false);
  const [particles, setParticles] = useState([]);

  // Generate floating particles
  useEffect(() => {
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 3 + Math.random() * 2,
    }));
    setParticles(newParticles);
  }, []);

  const lightDiya = (index) => {
    if (!litDiyas.includes(index)) {
      setLitDiyas([...litDiyas, index]);
      
      // Reveal coupon after lighting 3 diyas
      if (litDiyas.length + 1 >= 3) {
        setTimeout(() => {
          setCouponRevealed(true);
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }, 500);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0F081C] text-white overflow-hidden relative">
      {/* Animated Background Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute w-1 h-1 bg-[#FBBF24] rounded-full animate-float"
            style={{
              left: `${particle.left}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                backgroundColor: ['#FBBF24', '#F97316', '#F43F5E', '#10B981'][Math.floor(Math.random() * 4)],
                animationDelay: `${Math.random() * 0.5}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-[#1A0F2E]/80 backdrop-blur-md border-b border-[#FBBF24]/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Flame className="w-8 h-8 text-[#F97316]" />
              <h1 className="text-2xl font-bold text-[#FBBF24]">Roastey Snacks</h1>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/products')}
                variant="ghost"
                className="text-[#E2D9F3] hover:text-[#FBBF24]"
              >
                Shop Hampers
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-[#F59E0B] via-[#EA580C] to-[#BE123C] text-white hover:brightness-110"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-4" data-testid="hero-section">
        <div className="container mx-auto text-center">
          {/* Main Headline */}
          <div className="mb-8 animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FBBF24] via-[#F97316] to-[#F43F5E]">
                Light Up Your Diwali
              </span>
            </h1>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              with Roastey Healthy Delights!
            </h2>
            <p className="text-lg text-[#E2D9F3] max-w-2xl mx-auto">
              Celebrate the festival of lights with guilt-free gourmet snacks. 
              Premium roasted treats, zero trans fat, 100% delicious. 🪔✨
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              onClick={() => navigate('/products')}
              className="bg-gradient-to-r from-[#F59E0B] via-[#EA580C] to-[#BE123C] text-white hover:brightness-110 shadow-lg shadow-[#EA580C]/30 text-lg px-8 py-6"
            >
              <Gift className="mr-2 w-5 h-5" />
              Shop Diwali Hampers
            </Button>
            <Button
              onClick={() => navigate('/products')}
              className="bg-[#25143D] text-[#FBBF24] border border-[#FBBF24]/40 hover:bg-[#FBBF24]/10 text-lg px-8 py-6"
            >
              <Sparkles className="mr-2 w-5 h-5" />
              Explore Collection
            </Button>
          </div>

          {/* Decorative Diya Animation */}
          <div className="flex justify-center gap-8 mb-8">
            <div className="relative">
              <Flame className="w-16 h-16 text-[#F97316] animate-pulse" />
              <div className="absolute inset-0 bg-[#F97316] opacity-20 blur-xl animate-pulse" />
            </div>
            <div className="relative" style={{ animationDelay: '0.5s' }}>
              <Flame className="w-16 h-16 text-[#FBBF24] animate-pulse" />
              <div className="absolute inset-0 bg-[#FBBF24] opacity-20 blur-xl animate-pulse" />
            </div>
            <div className="relative" style={{ animationDelay: '1s' }}>
              <Flame className="w-16 h-16 text-[#F43F5E] animate-pulse" />
              <div className="absolute inset-0 bg-[#F43F5E] opacity-20 blur-xl animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Diya Lighting Section */}
      <section className="py-16 px-4 bg-[#1A0F2E]/50" data-testid="diya-lighting-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#FBBF24] mb-4">
              🪔 Light a Virtual Diya 🪔
            </h2>
            <p className="text-[#E2D9F3] text-lg">
              Click on the diyas below to light them and receive a special Diwali blessing!
            </p>
            <p className="text-sm text-[#9CA3AF] mt-2">
              Light 3 diyas to unlock a surprise gift 🎁
            </p>
          </div>

          {/* Interactive Diyas */}
          <div className="flex justify-center gap-6 sm:gap-12 mb-8 flex-wrap">
            {[0, 1, 2, 3, 4].map((index) => (
              <button
                key={index}
                onClick={() => lightDiya(index)}
                className={`group relative transition-all duration-500 cursor-pointer ${
                  litDiyas.includes(index) ? 'scale-110' : 'hover:scale-105'
                }`}
                aria-label={`Light diya ${index + 1}`}
              >
                <div className="relative">
                  {/* Diya Base */}
                  <div className="w-16 h-8 bg-gradient-to-b from-[#F59E0B] to-[#EA580C] rounded-full" />
                  
                  {/* Flame */}
                  {litDiyas.includes(index) ? (
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2">
                      <Flame className="w-8 h-8 text-[#F97316] animate-pulse" />
                      <div className="absolute inset-0 bg-[#F97316] opacity-30 blur-lg animate-pulse" />
                    </div>
                  ) : (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-2 h-4 bg-[#4B5563] rounded-full" />
                  )}
                  
                  {/* Glow Effect */}
                  {litDiyas.includes(index) && (
                    <div className="absolute inset-0 bg-[#F59E0B] opacity-20 blur-xl rounded-full scale-150 animate-pulse" />
                  )}
                </div>
                
                <div className="text-xs text-[#9CA3AF] mt-2 group-hover:text-[#FBBF24] transition-colors">
                  {litDiyas.includes(index) ? 'Lit ✨' : 'Click to light'}
                </div>
              </button>
            ))}
          </div>

          {/* Coupon Reveal */}
          {couponRevealed && (
            <Card className="max-w-md mx-auto bg-[#25143D] border-2 border-[#FBBF24] animate-bounce-once">
              <CardContent className="p-6 text-center">
                <Star className="w-12 h-12 text-[#FBBF24] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#FBBF24] mb-2">
                  🎉 Congratulations! 🎉
                </h3>
                <p className="text-[#E2D9F3] mb-4">
                  You've unlocked a special Diwali blessing!
                </p>
                <div className="bg-[#0F081C] p-4 rounded-lg border border-[#FBBF24]/40">
                  <p className="text-sm text-[#9CA3AF] mb-2">Your Exclusive Coupon Code:</p>
                  <p className="text-3xl font-bold text-[#FBBF24] tracking-wider">DIWALI20</p>
                  <p className="text-sm text-[#10B981] mt-2">Get 20% OFF on all products!</p>
                </div>
                <Button
                  onClick={() => navigate('/products')}
                  className="mt-4 bg-gradient-to-r from-[#F59E0B] via-[#EA580C] to-[#BE123C] text-white w-full"
                >
                  Shop Now & Use Coupon
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Diwali Wishes Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <Card className="max-w-3xl mx-auto bg-[#1A0F2E]/80 backdrop-blur-md border border-[#FBBF24]/20">
            <CardContent className="p-8 text-center">
              <Heart className="w-16 h-16 text-[#F43F5E] mx-auto mb-6 animate-pulse" />
              <h2 className="text-3xl font-bold text-[#FBBF24] mb-4">
                Wishing You & Your Family
              </h2>
              <p className="text-xl text-[#E2D9F3] leading-relaxed mb-6">
                A very Happy & Prosperous Diwali! ✨
                <br />
                <br />
                May this festival of lights bring joy, health, and countless blessings to your home.
                May your life be filled with happiness, success, and the warmth of loved ones.
                <br />
                <br />
                शुभ दीपावली! 🪔
              </p>
              <p className="text-sm text-[#9CA3AF]">
                From all of us at Roastey Snacks
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Featured Products Teaser */}
      <section className="py-16 px-4 bg-[#1A0F2E]/50">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Celebrate with Healthy Delights
          </h2>
          <p className="text-[#E2D9F3] mb-8 max-w-2xl mx-auto">
            Discover our specially curated Diwali collection featuring premium roasted snacks, 
            zero trans fat treats, and guilt-free indulgence for your celebrations.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { icon: '🥜', title: 'Roasted Nuts', desc: 'Premium Selection' },
              { icon: '🌾', title: 'Multigrain Mix', desc: 'Heart Healthy' },
              { icon: '🍪', title: 'Baked Cookies', desc: 'Guilt-Free' },
              { icon: '🎁', title: 'Gift Hampers', desc: 'Perfect for Gifting' }
            ].map((item, index) => (
              <Card key={index} className="bg-[#25143D] border border-[#FBBF24]/20 hover:border-[#FBBF24] transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-2">{item.icon}</div>
                  <h3 className="text-lg font-bold text-[#FBBF24] mb-1">{item.title}</h3>
                  <p className="text-sm text-[#9CA3AF]">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <Button
            onClick={() => navigate('/products')}
            className="bg-gradient-to-r from-[#F59E0B] via-[#EA580C] to-[#BE123C] text-white hover:brightness-110 shadow-lg text-lg px-8 py-6"
          >
            Explore All Products
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F081C] border-t border-[#FBBF24]/20 py-8 px-4">
        <div className="container mx-auto text-center">
          <p className="text-[#9CA3AF]">
            © 2025 Roastey Snacks. All rights reserved.
          </p>
          <p className="text-sm text-[#9CA3AF] mt-2">
            Wishing you a bright and beautiful Diwali! 🪔✨
          </p>
        </div>
      </footer>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh);
          }
        }

        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-once {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-float {
          animation: float linear infinite;
        }

        .animate-confetti {
          animation: confetti 2s ease-out forwards;
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-bounce-once {
          animation: bounce-once 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DiwaliLanding;
